import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { ActionSheet, ErrorCard, Skeleton, SkeletonTextBlock, Text } from '@fhs/ui';
import { useInRestaurantRedemptionContext } from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useMaxRedemptionLimitAlert } from '../../hooks/use-max-redemption-limit-alert';
import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

import { DisplayCard, DisplayCardProps } from './display-card';
import { CardContent } from './reward-tier-card-content';

function renderItem({ item }: { item: DisplayCardProps }) {
  return <DisplayCard {...item} />;
}

function renderSeparator() {
  return <View style={styles.separator} />;
}

function RewardTierSkeleton() {
  return (
    <DisplayCard
      style={styles.skeletonCard}
      image={<Skeleton style={styles.imageSkeleton} />}
      button={<Skeleton style={styles.buttonSkeleton} />}
    >
      <SkeletonTextBlock lines={2} lineStyle={{ marginBottom: 4 }} style={styles.skeletonText} />
    </DisplayCard>
  );
}

export function RedeemTab() {
  const { data, isError, refetch, isLoading: isLoadingIncentives } = useLoyaltyIncentives();
  const { loyaltyUserReady } = useLoyaltyLegacyStates();
  const isLoading = isLoadingIncentives || !loyaltyUserReady;

  const renderMaxRedemptionLimitAlert = useMaxRedemptionLimitAlert();

  const { setInStoreRewardTierId } = useInRestaurantRedemptionContext();
  const onPressRedeem = useCallback(
    (_rewardTierId: string) => {
      setInStoreRewardTierId(_rewardTierId);
    },
    [setInStoreRewardTierId]
  );

  const cardsToRender = useMemo(() => {
    return (
      data?.rewardTiers?.reduce<DisplayCardProps[]>((cards, rewardTier) => {
        if (rewardTier.isRedeemable) {
          cards.push({
            image: rewardTier.image,
            button: {
              label: 'Redeem',
              onPress: () => onPressRedeem(rewardTier.id),
              type: 'outline',
            },
            children: (
              <CardContent
                title={rewardTier.name}
                description={rewardTier.message}
                isRedeemable={rewardTier.isRedeemable}
              />
            ),
          });
        }
        return cards;
      }, []) ?? []
    );
  }, [data?.rewardTiers, onPressRedeem]);

  const renderHeader = useCallback(() => {
    return (
      <View style={styles.header}>
        <Text.Heading type="two">Redeem Rewards In-Restaurant</Text.Heading>
        <Text.Ui size="md">Choose what you want to redeem:</Text.Ui>
        {renderMaxRedemptionLimitAlert()}
      </View>
    );
  }, [renderMaxRedemptionLimitAlert]);

  const renderEmpty = useCallback(() => {
    if (isLoading) {
      return (
        <View>
          <RewardTierSkeleton />
          <RewardTierSkeleton />
          <RewardTierSkeleton />
        </View>
      );
    }
    const firstRewardTier = data?.rewardTiers?.[0];
    if (firstRewardTier) {
      return (
        <DisplayCard
          image={firstRewardTier.image}
          button={{
            label: 'Redeem',
            onPress: () => onPressRedeem(firstRewardTier.id),
            type: 'outline',
            disabled: true,
          }}
        >
          <CardContent
            title={firstRewardTier.name}
            description={firstRewardTier.message}
            isRedeemable={false}
          />
        </DisplayCard>
      );
    }

    return null;
  }, [data?.rewardTiers, onPressRedeem, isLoading]);

  if (isError) {
    return <ErrorCard buttonHandle={refetch} />;
  }

  return (
    <ActionSheet.FlatList
      style={styles.tab}
      contentContainerStyle={styles.scrollContent}
      ListHeaderComponent={renderHeader}
      ListEmptyComponent={renderEmpty}
      ItemSeparatorComponent={renderSeparator}
      data={cardsToRender}
      renderItem={renderItem}
    />
  );
}

const styles = StyleSheet.create({
  tab: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  header: {
    gap: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
  separator: {
    marginBottom: 12,
  },
  skeletonCard: {
    marginBottom: 12,
  },
  imageSkeleton: {
    width: 40,
    height: 40,
    borderRadius: 100,
  },
  buttonSkeleton: {
    width: 86,
    height: 34,
  },
  skeletonText: {
    width: '70%',
  },
});
